import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Toolbar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Box,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import { visuallyHidden } from "@mui/utils";
import { MdFilterList } from "react-icons/md";
import { createTheme } from "@mui/material/styles";

const theme = createTheme();

const useStyles = {
  root: {
    minWidth: 800,
    padding: theme.spacing(2),
  },
  paper: {
    width: "100%",
  },
  table: {
    minWidth: 800,
  },
  addUserButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  searchBar: {
    backgroundColor: "rgba(0, 0, 0, 0)",
    marginTop: theme.spacing(5),
  },
  searchInput: {
    fontSize: theme.typography.fontSize,
  },
  searchIcon: {
    marginLeft: theme.spacing(2),
    color: "rgba(0, 0, 0, 0.5)",
    display: "block",
  },
  tableHeader: {
    paddingTop: 10,
  },
  userTable: {},
  projectTable: {
    marginLeft: theme.spacing(5),
  },
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "id", numeric: false, disablePadding: true, label: "id" },
  { id: "type", numeric: true, disablePadding: false, label: "type" },
  {
    id: "path",
    numeric: true,
    disablePadding: false,
    label: "path",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="secondary"
            size="small"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all records",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => {
  const { numSelected, onAssignClick } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.secondary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Records
        </Typography>
      )}

      {numSelected > 0 ? (
        <FormControlLabel
          onClick={onAssignClick}
          style={{ paddingRight: 10, fontSize: 10 }}
          control={
            <Button
              sx={{ flex: "1 1 100%" }}
              color="secondary"
              variant="contained"
            >
              Assign
            </Button>
          }
          label={""}
          labelPlacement="start"
        />
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <MdFilterList />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

function EnhancedTable({ userId, projects, handleClose }) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("Id");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [projectId, setProjectId] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [search, setSearch] = React.useState("");
  const [files, setFiles] = React.useState([]);

  useEffect(() => {
    const getFiles = async (id) => {
      console.log(`/api/cada/event?pid=${id}`);
      const result = await axios(`/api/cada/event?pid=${id}`);
      console.log(result);
      setFiles(result.data);
    };

    if (projectId !== 0) {
      getFiles(projectId);
    }
  }, [projectId]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = files.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const handleFormChange = (event) => {
    setProjectId(event.target.value);
  };

  const handleAssignClick = () => {
    console.log("selected: ", selected);
    console.log("projectId: ", projectId);
    console.log("userId: ", userId);
    const getFiles = async (selected) => {
      console.log(`/api/cada/event/assignments?uid=${userId}`);
      const result = await axios.post(
        `/api/cada/event/assignments?uid=${userId}`,
        selected
      );
      console.log(result);
      if (result.status === 200) {
        handleClose();
      }
    };

    getFiles(selected);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, files.length - page * rowsPerPage);

  return (
    <div style={{ ...useStyles.root }}>
      <Grid item md={3} xs={3}>
        <TextField
          select
          fullWidth
          label="Project"
          margin="dense"
          name="cadaProjectId"
          onChange={handleFormChange}
          required
          value={projectId ? projectId : ""}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        >
          {projects.map((option) => (
            <MenuItem
              key={Object.keys(option)[0]}
              value={Object.keys(option)[0]}
            >
              {Object.values(option)[0]}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {projectId !== 0 && (
        <Paper style={{ ...useStyles.paper }}>
          <EnhancedTableToolbar
            numSelected={selected.length}
            onAssignClick={handleAssignClick}
          />
          <TableContainer>
            <Table
              style={{ ...useStyles.table }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={files.length}
              />
              <TableBody>
                {stableSort(files, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row.id)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": labelId }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none"
                        >
                          {row.id}
                        </TableCell>
                        <TableCell align="right">{"-"}</TableCell>
                        <TableCell align="right">
                          {row.cadaFile.path}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={files.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </div>
  );
}

export default EnhancedTable;
